import { useEffect, useRef, useCallback } from "react";

interface UseAutoScanInputOptions {
  inputRef: React.RefObject<HTMLInputElement>;
  enabled: boolean;
  onScan: (code: string) => void;
  minLength?: number;
  maxDelay?: number; // ms
  preventDefault?: boolean;
}

/**
 * Hook for automatically detecting input from external devices
 * (barcode scanner, NFC reader, RFID reader, etc.)
 *
 * These devices work as keyboard emulators and type fast
 * (10-100ms between characters), which differentiates them from humans (200-500ms)
 */
export const useAutoScanInput = ({
  inputRef,
  enabled,
  onScan,
  minLength = 4,
  maxDelay = 100,
  preventDefault = true,
}: UseAutoScanInputOptions) => {
  const bufferRef = useRef("");
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const timestampRef = useRef<number>(0);

  const clearBuffer = useCallback(() => {
    bufferRef.current = "";
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled || !inputRef.current) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const now = Date.now();
      const timeSinceLastKey = now - timestampRef.current;
      timestampRef.current = now;

      // reset buffer if too much time passed
      if (timeSinceLastKey > maxDelay * 2 && bufferRef.current) {
        bufferRef.current = "";
      }

      if (event.key === "Enter" || event.key === "Tab") {
        if (preventDefault) event.preventDefault();

        if (bufferRef.current.length >= minLength) {
          const scannedCode = bufferRef.current.trim();
          onScan(scannedCode);
        }

        clearBuffer();
        return;
      }

      if (event.key.length !== 1) return;

      if (preventDefault) event.preventDefault();

      bufferRef.current += event.key;

      // auto-submit after maxDelay if no Enter
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => {
        if (bufferRef.current.length >= minLength) {
          const scannedCode = bufferRef.current.trim();
          onScan(scannedCode);
        }
        clearBuffer();
      }, maxDelay);
    };

    const el = inputRef.current;
    el.addEventListener("keydown", handleKeyDown);

    // eslint-disable-next-line consistent-return
    return () => {
      el.removeEventListener("keydown", handleKeyDown);
      clearBuffer();
    };
  }, [
    inputRef,
    enabled,
    onScan,
    minLength,
    maxDelay,
    preventDefault,
    clearBuffer,
  ]);
};
